import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { TID } from "@/lib/constants";
import { toast } from "sonner";

export default function AdminGazetteEditor() {
  const { id } = useParams();
  const editing = !!id;
  const [form, setForm] = useState({ title: "", body: "", is_pinned: false });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const list = await adminApi.listGazette();
        const p = (list.posts || []).find((x) => x.post_id === id);
        if (p) setForm({ title: p.title, body: p.body, is_pinned: !!p.is_pinned });
      } catch { toast.error("Failed to load post"); }
    })();
  }, [id, editing]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title and body required");
    setBusy(true);
    try {
      if (editing) await adminApi.updateGazette(id, form);
      else await adminApi.createGazette(form);
      toast.success(editing ? "Updated" : "Published");
      navigate("/studio");
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#111622]">
      <form onSubmit={save} className="mx-auto w-full max-w-[720px] px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-[#F8FAFC]">{editing ? "Edit Gazette Post" : "New Gazette Post"}</h1>
          <Link to="/studio" className="text-xs text-[#94A3B8] hover:text-[#E85A2A] transition-colors">← Back</Link>
        </div>

        <div className="mt-5 rounded-2xl bg-[#1A2232] border border-[#26334D] p-5 sm:p-6">
          <label className="text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">Title</label>
          <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="mt-2 h-11 w-full rounded-xl border border-[#26334D] bg-[#1A2232] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E85A2A]" />

          <label className="mt-4 block text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">Body</label>
          <textarea required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
            className="mt-2 min-h-[160px] w-full rounded-2xl border border-[#26334D] bg-[#1A2232] p-4 text-sm text-[#F8FAFC] focus:outline-none focus:ring-2 focus:ring-[#E85A2A]" style={{ lineHeight: 1.6 }} />

          <label className="mt-4 flex items-center gap-2 text-sm text-[#F8FAFC]">
            <input type="checkbox" checked={form.is_pinned} onChange={(e) => setForm({ ...form, is_pinned: e.target.checked })} />
            Pin at top
          </label>
        </div>

        <button data-testid={TID.adminSave} disabled={busy} type="submit"
          className="mt-5 w-full sm:w-auto rounded-xl bg-[#E85A2A] text-[#F8FAFC] px-5 py-3 text-sm font-semibold hover:bg-[#C94A1D] transition-colors disabled:opacity-50">
          {busy ? "Saving…" : (editing ? "Update" : "Publish")}
        </button>
      </form>
    </div>
  );
}
